import type * as React from "react";
import { cn } from "./lib/cn";

const controlClass =
  "min-h-11 w-full rounded-control border border-border bg-surface px-3 py-2 text-base text-text placeholder:text-text-muted/80 transition-colors duration-150 hover:border-information-border focus:border-participation focus:outline-none focus:ring-2 focus:ring-participation/25 disabled:cursor-not-allowed disabled:bg-surface-disabled disabled:text-text-muted aria-invalid:border-error aria-invalid:ring-2 aria-invalid:ring-error/20 motion-reduce:transition-none";

export function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input data-slot="input" className={cn(controlClass, className)} {...props} />;
}

export function Textarea({
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(controlClass, "min-h-28 resize-y", className)}
      {...props}
    />
  );
}

export function Select({
  className,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select data-slot="select" className={cn(controlClass, "cursor-pointer", className)} {...props}>
      {children}
    </select>
  );
}

interface ChoiceProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  type: "checkbox" | "radio";
}

function Choice({ className, type, ...props }: ChoiceProps) {
  return (
    <span className="inline-flex size-11 shrink-0 items-center justify-center">
      <input
        type={type}
        className={cn(
          "accent-participation size-5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60",
          className,
        )}
        {...props}
      />
    </span>
  );
}

export function Checkbox(props: Omit<ChoiceProps, "type">) {
  return <Choice type="checkbox" {...props} />;
}

export function Radio(props: Omit<ChoiceProps, "type">) {
  return <Choice type="radio" {...props} />;
}

export function Field({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("space-y-2", className)} {...props} />;
}

export function FieldLabel({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn("text-text block text-base font-medium", className)} {...props} />;
}

export function FieldDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-small text-text-muted", className)} {...props} />;
}

export function FieldError({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p role="alert" className={cn("text-small text-error font-medium", className)} {...props} />
  );
}
